/**
 * Passwords are stretched in the browser, not on the server.
 *
 * The browser runs PBKDF2-SHA256 with 600 000 iterations (the OWASP
 * recommendation) over the password, salted with the username, and sends only
 * the resulting 256-bit key. The server stores a salted hash of that key. A
 * stolen database therefore still costs an attacker 600 000 iterations per
 * password guess — while each login costs the server almost no CPU, which
 * keeps Studio inside Cloudflare's free-plan limit of 10 ms CPU per request.
 * The password itself never leaves the device.
 */

export const KEY_ITERATIONS = 600_000;
let iterations = KEY_ITERATIONS;

/** Tests only: derive faster. */
export function _setKeyIterationsForTests(n: number) {
  iterations = n;
}

const normalizeName = (username: string) => username.trim().toLowerCase();

function b64url(bytes: Uint8Array): string {
  let s = '';
  for (const x of bytes) s += String.fromCharCode(x);
  return btoa(s).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

export async function passwordKey(username: string, password: string): Promise<string> {
  const enc = new TextEncoder();
  const material = await crypto.subtle.importKey('raw', enc.encode(password.normalize('NFKC')), 'PBKDF2', false, ['deriveBits']);
  const bits = await crypto.subtle.deriveBits(
    { name: 'PBKDF2', hash: 'SHA-256', salt: enc.encode(`studio.password.v1:${normalizeName(username)}`), iterations },
    material,
    256,
  );
  return b64url(new Uint8Array(bits));
}

const COMMON_PASSWORDS = new Set([
  'password', 'password1', 'passord', 'passord1', 'passord123', '12345678', '123456789', '1234567890', '87654321',
  'qwertyui', 'qwerty123', 'qwertyuiop', 'asdfghjk', 'iloveyou', 'abcdefgh', 'abc12345', 'football', 'fotball1',
  'studio123', 'skole123', 'hemmelig', 'sommer2025', 'sommer2026', 'vinter2026', 'norge123', 'minecraft',
]);

/** Returns an error code (see apiMessage) if the password is too weak, else null. */
export function passwordProblem(password: string, username: string): string | null {
  if (password.length < 8) return 'password_too_short';
  if (password.length > 200) return 'password_too_long';
  const lower = password.toLowerCase();
  if (lower.replace(/\d+$/, '') === normalizeName(username) || COMMON_PASSWORDS.has(lower) || /^(.)\1+$/.test(password)) return 'password_too_common';
  return null;
}
